import { CoralogixRum } from "@coralogix/browser";
import { dmnIdentity } from "../utils/dmnIdentity";
import {
  getEnvironmentProps,
  isBrowser,
  isLocalDev,
  isNativeTab
} from "../utils/environment";
import { labelEventOrigin } from "./eventOrigin";

// Shared with native/telemetry/config.ts — update both if rotated.
const CORALOGIX_PUBLIC_KEY = "cxtp_hBsf84TZZU6Gm5RpMRwdIZJ8J2FbeB";
const SUBSYSTEM_NAME = isBrowser() ? "CollinsWeb2.0" : "CollinsMobile";
const PE_CODE = "396";

/**
 * Determine how the user is accessing Collins.
 *
 * `isNativeTab()` detects any cross-origin iframe — currently only EVO
 * Workspace embeds Collins this way, but the check is not EVO-specific.
 */
const resolveAccessChannel = (): string => {
  if (!isBrowser()) return "Native App";
  if (isNativeTab()) return "EVO Workspace";
  return "Direct Web";
};

const getInitLabels = (): Record<string, string> => ({
  "subsystem.name": SUBSYSTEM_NAME,
  "pe.code": PE_CODE,
  "access.channel": resolveAccessChannel()
});

let sessionLabels: Record<string, string> = {};

const mergeSessionLabels = (
  labels: Record<string, string>
): Record<string, string> => {
  sessionLabels = { ...sessionLabels, ...labels, ...getInitLabels() };
  return sessionLabels;
};

export const mapEnvName = (env: string): string => {
  switch (env.toLowerCase()) {
    case "development":
      return "Dev";
    case "production":
      return "Production";
    case "pre-prod":
      return "PreProd";
    case "staging":
      return "Staging";
    case "demo":
      return "Demo";
    default:
      return env;
  }
};

let initialised = false;

/**
 * Resolve the Coralogix `application` field.
 *
 * The application name includes the detected environment so that
 * source-map uploads (done only for Production in the release pipeline)
 * match the runtime application name exactly.
 */
async function resolveApplicationContext(): Promise<{
  application: string;
  environment: string;
}> {
  const token = await dmnIdentity.getAccessToken();
  const env = getEnvironmentProps(token);
  const environment = mapEnvName(env.ENVIRONMENT ?? "development");
  return { application: `${SUBSYSTEM_NAME}.${environment}`, environment };
}

/**
 * Initialise the Coralogix RUM SDK.
 *
 * Call this once, as early as possible (before React renders) so that
 * automatic instrumentation captures everything from the start.
 */
export async function initialiseTelemetry(): Promise<void> {
  if (initialised) return;

  try {
    const { application, environment } = await resolveApplicationContext();
    const version = process.env.BUILD_BUILDNUMBER ?? "development";

    CoralogixRum.init({
      public_key: CORALOGIX_PUBLIC_KEY,
      application,
      version,
      coralogixDomain: "EU2",
      environment,
      labels: mergeSessionLabels({}),
      beforeSend: labelEventOrigin,
      sessionRecordingConfig: isLocalDev()
        ? undefined
        : {
            enable: true,
            autoStartSessionRecording: true
          }
    });

    initialised = true;
  } catch (err) {
    console.error("[Telemetry] Failed to initialise Coralogix RUM SDK:", err);
  }
}

/**
 * Report an error to the Coralogix telemetry backend.
 *
 * Uses `CoralogixRum.error()` for log-based error reporting and
 * `CoralogixRum.captureError()` for structured error capture with
 * additional metadata.
 */
export function reportTelemetryError(
  error: Error,
  attributes?: Record<string, string>
): void {
  if (isLocalDev()) {
    // Avoid reporting errors in local development to prevent cluttering Coralogix with non-actionable data
    console.warn(
      "[Telemetry] Skipping error report in local development:",
      error
    );
    return;
  }

  try {
    const labels = attributes?.screenshotId
      ? { screenshotId: attributes.screenshotId }
      : undefined;
    CoralogixRum.captureError(error, attributes, labels);
  } catch (err) {
    console.error("[Telemetry] Failed to report error:", err);
  }
}

/**
 * Report failures from third-party initialisers under a named title so they are attributable rather than masked.
 */
export function reportThirdPartyInitError(
  errorTitle: string,
  error: unknown
): void {
  const normalisedError =
    error instanceof Error ? error : new Error(String(error));
  reportTelemetryError(normalisedError, { errorTitle });
}

/**
 * Report a warning to the Coralogix telemetry backend.
 *
 * For expected failures that are not user-facing errors (e.g. a role-gated
 * endpoint answering 403) — surfaces in telemetry without the noise of an
 * error event, toast or screenshot.
 */
export function reportTelemetryWarning(
  message: string,
  attributes?: Record<string, string>
): void {
  if (isLocalDev()) {
    // Avoid reporting warnings in local development to prevent cluttering Coralogix with non-actionable data
    console.warn(
      "[Telemetry] Skipping warning report in local development:",
      message
    );
    return;
  }

  try {
    CoralogixRum.warn(message, attributes);
  } catch (err) {
    console.error("[Telemetry] Failed to report warning:", err);
  }
}

export function captureScreenshot(description?: string): string | undefined {
  if (isLocalDev()) {
    // Avoid capturing screenshots in local development to prevent cluttering Coralogix with non-actionable data
    return undefined;
  }

  try {
    return CoralogixRum.screenshot(description);
  } catch (err) {
    console.error("[Telemetry] Failed to capture screenshot:", err);
    return undefined;
  }
}

/**
 * Send a custom measurement to Coralogix RUM.
 *
 * Emits a `custom-measurement` RUM event, queryable in DataPrime by
 * `$d.custom_measurement_context.name`/`.value`. Measurement names carry
 * no per-event dimensions, so split a measurement by session labels
 * (see `setTelemetryLabels`) rather than by encoding values in the name.
 */
export function reportCustomMeasurement(name: string, value: number): void {
  if (isLocalDev()) {
    // Avoid reporting measurements in local development to prevent cluttering Coralogix with non-actionable data
    return;
  }

  if (!Number.isFinite(value)) return;

  try {
    CoralogixRum.sendCustomMeasurement(name, value);
  } catch (err) {
    console.error("[Telemetry] Failed to send custom measurement:", err);
  }
}

/**
 * Set user context on the Coralogix RUM session.
 *
 * This associates all subsequent events with the given user, replacing
 * the previous `setTelemetryAttribute` approach.
 */
export function setUserContext(
  userId: string,
  userName: string,
  userEmail?: string,
  metadata?: Record<string, string>
): void {
  try {
    CoralogixRum.setUserContext({
      user_id: userId,
      user_name: userName,
      user_email: userEmail,
      user_metadata: metadata
    });
  } catch (err) {
    console.error("[Telemetry] Failed to set user context:", err);
  }
}

/**
 * Set labels on the Coralogix RUM session.
 *
 * Labels merge into the session's accumulated labels because the SDK's
 * `setLabels` replaces the whole set. Init labels (`subsystem.name`,
 * `pe.code`, `access.channel`) cannot be overridden.
 */
export function setTelemetryLabels(labels: Record<string, string>): void {
  try {
    CoralogixRum.setLabels(mergeSessionLabels(labels));
  } catch (err) {
    console.error("[Telemetry] Failed to set labels:", err);
  }
}
